<template>
  <div class="terms-list__wrapper">
    <a-search-result-section-header
      :title="title"
      :description="termCountString"
    />

    <div class="terms-list__result">
      <ul v-if="isTermListFilled" class="terms-list__result-table">
        <li
          v-for="(term, index) in termList"
          :key="`${term}-${index}`"
          :class="getTermClass(index)"
        >
          <h2 class="term-link__wrapper normal">
            <a-link :to="generateTermRoute(term)" class="term-link">
              {{ term.Name }}
            </a-link>
          </h2>
        </li>
      </ul>

      <p v-else class="terms-list__not-found">
        No terms found
      </p>

      <a-see-more-less
        v-if="isShowMoreVisible"
        class="terms-list__see-more"
        :is-expanded="false"
        :in-progress="inProgress"
        @input="getMoreTerms"
      />
    </div>
  </div>
</template>

<script>
import { propValidator, PROP_TYPES } from '@/utils/validators'
import mixins from '@/utils/mixins'
import ASearchResultSectionHeader from 'shared/ASearchResultSectionHeader'
import { hydrateWhenVisible } from '@/utils/helpers/vue-lazy-hydration/LazyHydrate'

export default {
  name: 'ATermsSearchResult',
  components: {
    ASeeMoreLess: hydrateWhenVisible(() => import('shared/ASeeMoreLess'), {
      props: ['is-expanded', 'in-progress']
    }),
    ASearchResultSectionHeader
  },
  mixins: [mixins.urlGenerators],
  props: {
    letter: propValidator([PROP_TYPES.STRING], false, 'a'),
    title: propValidator([PROP_TYPES.STRING], false),
    termList: propValidator([PROP_TYPES.ARRAY]),
    termCount: propValidator([PROP_TYPES.NUMBER], false, null),
    pageCount: propValidator([PROP_TYPES.NUMBER]),
    take: propValidator([PROP_TYPES.NUMBER], false, 20),
    inProgress: propValidator([PROP_TYPES.BOOLEAN], false, false)
  },
  data() {
    return {
      page: 0
    }
  },
  computed: {
    isShowMoreVisible() {
      return this.pageCount - 1 > this.page || this.inProgress
    },
    isTermListFilled() {
      return !!this.termList.length
    },
    termCountString() {
      return this.termCount === null ? '' : `${this.termCount} terms`
    }
  },
  methods: {
    getTermClass(index) {
      return [
        'terms-list__link-container',
        index % 2 === 0 ? 'terms-list__left-column' : 'terms-list__right-column'
      ]
    },
    getMoreTerms() {
      this.$emit('request-terms', {
        take: this.take,
        page: ++this.page
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.terms-list__wrapper {
  margin-bottom: 25px;

  .term-link__wrapper {
    line-height: 22px;
  }

  .terms-list__letter {
    text-transform: uppercase;
  }

  .terms-list__link-container {
    padding-top: 45px;

    @include mobile {
      padding-top: 40px;
    }
  }

  .terms-list__result-table {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    margin-top: 20px;
    padding: 0 20px;

    @include desktop-md {
      padding: 0;
    }
  }

  .terms-list__left-column {
    width: 50%;
    padding-right: 80px;
    border-right: 2px solid $c--gray-cloud;

    &:nth-child(1) {
      padding-top: 20px;
    }

    @include desktop-md {
      padding-right: 30px;
    }

    @include mobile {
      width: 100%;
      padding-right: 0;
      border-right: none;
    }
  }

  .terms-list__right-column {
    width: 50%;
    padding-left: 80px;

    &:nth-child(2) {
      padding-top: 20px;
    }

    @include desktop-md {
      padding-left: 30px;
    }

    @include mobile {
      width: 100%;
      padding-left: 0;
      border-left: none;

      &:nth-child(2) {
        padding-top: 40px;
      }
    }
  }

  .terms-list__see-more {
    margin: 30px auto 0 auto;
    height: 32px;

    @include mobile {
      margin: 30px auto 0 auto;
    }
  }

  .terms-list__not-found {
    text-align: center;
    font-size: 20px;
    padding: 20px;
  }

  .terms-list__result-table .term-link:hover {
    color: $c--gray-main;
  }
}
</style>
